import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Minimal checkbox on a native `<input type="checkbox">` — the admin keeps
 * zero headless-UI dependencies (see `switch.tsx`, `tabs.tsx`), so no Radix.
 * Fully controlled: `checked` in, `onCheckedChange` out. `accent-color`
 * follows the theme's primary token so a tick matches the switch.
 */
export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'checked'> {
  checked: boolean;
  /** Half-ticked ("some, not all"). Only settable from script on a native input, hence the ref. */
  indeterminate?: boolean;
  onCheckedChange: (checked: boolean) => void;
}

export function Checkbox({ checked, indeterminate = false, onCheckedChange, className, ...props }: CheckboxProps) {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-checked={indeterminate ? 'mixed' : checked}
      onChange={(e) => onCheckedChange(e.target.checked)}
      className={cn(
        'size-4 shrink-0 cursor-pointer rounded-sm border border-input accent-primary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}
